/**
 * Fumigation application validation.
 *
 * Ported from the legacy WebForms ValidateFormInfo() routine in
 * features/application/CustomerInformation/validation-legacy.cs
 *
 * Rule sources (legacy line numbers):
 *  - Header fields ....... 1206-1251
 *  - Per-line rules ...... 1252-2016 (line 01 block repeated for lines 02-10)
 *  - Terms acceptance .... 2017-2021
 *  - At least one row .... 2022-2026
 */

import { applicationData, FumigationRecord } from "contexts/applicationDataFieldTypes";

// ── Messages (verbatim from legacy, minus the trailing "; <br />") ─────────────

export const VALIDATION_MESSAGES = {
    COMPANY_NAME: 'Please provide valid company name',
    CONTACT_NAME: 'Please provide valid contact name',
    PHONE: 'Please provide valid phone number',
    EMAIL: 'Please provide valid Email Address',
    CONTRACTOR_EMAIL: 'Please provide valid Contractor Email Address',
    TERMS: 'Please Accept Terms & Conditions',
    NO_ROWS: 'Please provide at least 1 customer / premise information for schedule',
    LINE_SCHEDULE_DATE: 'Please provide Close order schedule date or Restore Schedule date',
    LINE_CANCEL_DATE: 'Please provide Close order cancellation date or Restore Cancellation date',
    LINE_RESTORE_GAP: 'Backon schedule date must be 2 days greater than Close schedule date',
    LINE_METER_QTY: 'Please provide valid Meter quantity',
    LINE_CODE: 'Please provide a valid code',
    LINE_CODE_TYPE: 'Please Select Code Type',
} as const;

/** Legacy email pattern, preserved exactly (legacy line 1187). */
const EMAIL_REGEX =
    /^[a-zA-Z][\w.-]*[a-zA-Z0-9]@[a-zA-Z0-9][\w.-]*[a-zA-Z0-9]\.[a-zA-Z][a-zA-Z.]*[a-zA-Z]$/;

/** Minimum days between the close date and the restore (back-on) date. */
const MIN_RESTORE_GAP_DAYS = 2;

/** Meter quantity must land strictly inside this range (legacy: > 0 && < 99). */
const METER_QTY_MIN_EXCLUSIVE = 0;
const METER_QTY_MAX_EXCLUSIVE = 99;

// ── Result shape ──────────────────────────────────────────────────────────────

/** Keys used to flag individual header inputs. */
export type HeaderFieldKey =
    | 'fumigationCompanyName'
    | 'contactPerson'
    | 'telephoneNumber'
    | 'emailAddress'
    | 'alternateEmailAddress';

/** Keys used to flag individual cells within a row. */
export type RecordFieldKey =
    | 'closeSchedule'
    | 'restoreSchedule'
    | 'qty'
    | 'code'
    | 'codeType';

export interface ValidationResult {
    isValid: boolean;
    /** Header-level messages, in legacy order. */
    formErrors: string[];
    /** Row-level messages, already prefixed with "Line NN - ". */
    lineErrors: string[];
    /** Terms acceptance message, empty when accepted. */
    termsError: string;
    /** Header field -> message, for inline highlighting. */
    headerFieldErrors: Partial<Record<HeaderFieldKey, string>>;
    /** Row index -> (field -> message), for inline highlighting. */
    recordFieldErrors: Record<number, Partial<Record<RecordFieldKey, string>>>;
}

export const EMPTY_VALIDATION_RESULT: ValidationResult = {
    isValid: true,
    formErrors: [],
    lineErrors: [],
    termsError: '',
    headerFieldErrors: {},
    recordFieldErrors: {},
};

// ── Helpers ───────────────────────────────────────────────────────────────────

const trim = (value?: string | null): string => (value ?? '').trim();

/** Label a row the way the legacy form did: 1 -> "Line 01". */
export const lineLabel = (rowIndex: number): string =>
    `Line ${String(rowIndex + 1).padStart(2, '0')}`;

/** Parses the "MM/dd/yyyy" or "MM/dd/yyyy HH:mm" values from the schedule dropdowns.
 *  The time part (e.g. " AM", " AD") is stripped before parsing.
 */
const parseScheduleDate = (value?: string): Date | null => {
    const raw = trim(value);
    if (!raw) return null;
    // Strip anything after the date portion (time suffix like " AM", " AD", " PM")
    const datePart = raw.split(' ')[0];
    const parts = datePart.split('/');
    if (parts.length !== 3) return null;
    const [month, day, year] = parts.map(Number);
    if (!month || !day || !year) return null;
    const parsed = new Date(year, month - 1, day);
    return isNaN(parsed.getTime()) ? null : parsed;
};

const daysBetween = (later: Date, earlier: Date): number =>
    Math.round((later.getTime() - earlier.getTime()) / (24 * 60 * 60 * 1000));

/**
 * A row is only validated once it has an address — matching the legacy check
 * `if (fumLnNNAddress.Text.Trim() == "") swValLnNN = "E";`
 */
export const isRowInUse = (record: FumigationRecord): boolean =>
    Boolean(trim(record.premiseAddress));

/** Treats the placeholder "None" option the same as an empty selection. */
const isNoneOrEmpty = (value?: string): boolean => {
    const raw = trim(value);
    return !raw || raw === 'None';
};

export const isValidEmail = (value: string): boolean => EMAIL_REGEX.test(trim(value));

// ── Row validation ────────────────────────────────────────────────────────────

interface RowValidation {
    messages: string[];
    fieldErrors: Partial<Record<RecordFieldKey, string>>;
}

const validateRecord = (record: FumigationRecord, rowIndex: number): RowValidation => {
    const messages: string[] = [];
    const fieldErrors: Partial<Record<RecordFieldKey, string>> = {};
    const label = lineLabel(rowIndex);

    const add = (message: string, ...fields: RecordFieldKey[]) => {
        messages.push(`${label} - ${message}`);
        fields.forEach(field => {
            fieldErrors[field] = message;
        });
    };

    const orderType = trim(record.aer);
    const closeMissing = isNoneOrEmpty(record.closeSchedule);
    const restoreMissing = isNoneOrEmpty(record.restoreSchedule);

    if (orderType === 'Sched') {
        if (closeMissing && restoreMissing) {
            add(VALIDATION_MESSAGES.LINE_SCHEDULE_DATE, 'closeSchedule', 'restoreSchedule');
        }
        if (!closeMissing && !restoreMissing) {
            const closeDate = parseScheduleDate(record.closeSchedule);
            const restoreDate = parseScheduleDate(record.restoreSchedule);
            if (closeDate && restoreDate && daysBetween(restoreDate, closeDate) < MIN_RESTORE_GAP_DAYS) {
                add(VALIDATION_MESSAGES.LINE_RESTORE_GAP, 'restoreSchedule');
            }
        }
    } else if (orderType === 'Cancel') {
        if (closeMissing && restoreMissing) {
            add(VALIDATION_MESSAGES.LINE_CANCEL_DATE, 'closeSchedule', 'restoreSchedule');
        }
    }

    // Meter quantity must parse to a whole number in (0, 99).
    const qty = Number(record.qty);
    const qtyIsWhole = Number.isInteger(qty);
    if (!qtyIsWhole || qty <= METER_QTY_MIN_EXCLUSIVE || qty >= METER_QTY_MAX_EXCLUSIVE) {
        add(VALIDATION_MESSAGES.LINE_METER_QTY, 'qty');
    }

    // Code and code type must be supplied together.
    const codeTypeSelected = !isNoneOrEmpty(record.codeType);
    const codeEntered = Boolean(trim(record.code));
    if (codeTypeSelected && !codeEntered) {
        add(VALIDATION_MESSAGES.LINE_CODE, 'code');
    }
    if (!codeTypeSelected && codeEntered) {
        add(VALIDATION_MESSAGES.LINE_CODE_TYPE, 'codeType');
    }

    return { messages, fieldErrors };
};

// ── Entry point ───────────────────────────────────────────────────────────────

export const validateFumigationApplication = (
    data: applicationData,
    termsAccepted: boolean,
): ValidationResult => {
    const formErrors: string[] = [];
    const lineErrors: string[] = [];
    const headerFieldErrors: Partial<Record<HeaderFieldKey, string>> = {};
    const recordFieldErrors: Record<number, Partial<Record<RecordFieldKey, string>>> = {};

    const addHeaderError = (message: string, field: HeaderFieldKey) => {
        formErrors.push(message);
        // Last write wins for inline field highlighting — matches legacy single error icon.
        headerFieldErrors[field] = message;
    };

    // ── Header fields ─────────────────────────────────────────
    if (!trim(data.fumigationCompanyName)) {
        addHeaderError(VALIDATION_MESSAGES.COMPANY_NAME, 'fumigationCompanyName');
    }

    if (!trim(data.contactPerson)) {
        addHeaderError(VALIDATION_MESSAGES.CONTACT_NAME, 'contactPerson');
    }

    // Legacy checks area code, mid-3 and last-4 with three independent if-blocks,
    // appending the same message each time one is blank. Reproduced exactly.
    if (!trim(data.telephoneAreaCode)) {
        addHeaderError(VALIDATION_MESSAGES.PHONE, 'telephoneNumber');
    }
    if (!trim(data.telephoneMid3)) {
        addHeaderError(VALIDATION_MESSAGES.PHONE, 'telephoneNumber');
    }
    if (!trim(data.telephoneLast4)) {
        addHeaderError(VALIDATION_MESSAGES.PHONE, 'telephoneNumber');
    }

    const email = trim(data.emailAddress);
    if (!email || !isValidEmail(email)) {
        addHeaderError(VALIDATION_MESSAGES.EMAIL, 'emailAddress');
    }

    // Alternate ("contractor") email is optional, but must be valid when present.
    const alternateEmail = trim(data.alternateEmailAddress);
    if (alternateEmail && !isValidEmail(alternateEmail)) {
        addHeaderError(VALIDATION_MESSAGES.CONTRACTOR_EMAIL, 'alternateEmailAddress');
    }

    // ── Rows ──────────────────────────────────────────────────
    const records = data.records ?? [];
    const rowsInUse = records.filter(isRowInUse);

    records.forEach((record, rowIndex) => {
        if (!isRowInUse(record)) return;
        const { messages, fieldErrors } = validateRecord(record, rowIndex);
        if (messages.length) {
            lineErrors.push(...messages);
            recordFieldErrors[rowIndex] = fieldErrors;
        }
    });

    if (rowsInUse.length === 0) {
        lineErrors.push(VALIDATION_MESSAGES.NO_ROWS);
    }

    // ── Terms ─────────────────────────────────────────────────
    const termsError = termsAccepted ? '' : VALIDATION_MESSAGES.TERMS;

    return {
        isValid: formErrors.length === 0 && lineErrors.length === 0 && !termsError,
        formErrors,
        lineErrors,
        termsError,
        headerFieldErrors,
        recordFieldErrors,
    };
};
